<template>
  <template v-if="$route.name === 'students.index'">
    <div class="container-fluid py-4" dir="rtl">
      <div class="d-flex justify-content-end mb-2">
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="printStudentDataReport"
          :disabled="isPrintingReport"
        >
          <span v-if="isPrintingReport"><i class="fas fa-spinner fa-spin me-1"></i>{{ t('common.loading') }}</span>
          <span v-else><i class="fas fa-print me-1"></i>{{ t('students.print_list') }}</span>
        </button>
      </div>

      <!-- Filter Controls -->
      <StudentFilter
        :filters="filters"
        :classes="classes"
        @update:filters="updateFilters"
        @apply-filters="applyFilters"
        @reset-filters="resetFilters"
      />

      <!-- Student Table -->
      <StudentTable
        :students="students"
        :loading="isLoading"
        @edit="openEditDialog"
        @delete="openDeleteDialog"
      />
    </div>

    <PaginationControls
      :pagination="pagination"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />

    <!-- Dialogs -->
    <DeleteStudentDialog ref="deleteDialog" @delete="handleDeleteStudent" />
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import StudentTable from "@/components/features/students/table/StudentTable.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import DeleteStudentDialog from "@/components/features/students/dialogs/DeleteStudentDialog.vue";
import StudentFilter from "@/components/features/students/filters/StudentFilter.vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import api from "@/api";
import { useTableExport, formatPrintDate } from "@/composables/useTableExport";
const { t, locale } = useI18n();
const { printTable, openPrintWindow } = useTableExport();

const store = useStore();
const toast = useToast();
const isLoading = ref(false);

// State
const students = computed(() => store.state.students.students);
const pagination = computed(() => store.state.students.pagination);
const filters = computed(() => store.state.students.filters);
const classes = computed(() => store.state.classes.classes);

// Dialog refs
const deleteDialog = ref(null);

// Methods
const fetchStudents = async (page = 1, perPage = 10) => {
  isLoading.value = true;
  try {
    await store.dispatch("students/fetchStudents", { page, perPage });
  } catch (error) {
    notifyError(error, "students.load_failed"); 
  } finally {
    isLoading.value = false;
  }
};
function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden')); // 👈 show Forbidden
  return toast.error(extractApiMessage(error, fallbackKey));
}

// Updated to use mutations instead of actions
const updateFilters = (newFilters) => {
  store.commit("students/SET_FILTERS", newFilters);
};

const applyFilters = () => {
  fetchStudents(1, pagination.value.perPage);
};

const resetFilters = () => {
  store.commit("students/RESET_FILTERS");
  fetchStudents(1, pagination.value.perPage);
};

const handlePageChange = (page) => {
  fetchStudents(page, pagination.value.perPage);
};

const handlePerPageChange = (perPage) => {
  fetchStudents(1, perPage);
};

const openDeleteDialog = (studentId, studentName) => {
  deleteDialog.value.openDialog(studentId, studentName);
};

const handleDeleteStudent = async (studentId) => {
  try {
    const resp = await store.dispatch("students/deleteStudent", studentId);

    // Normalize status (works for axios responses or custom returns)
    const status = resp?.status ?? resp?.data?.status ?? null;

    // If the action returned nothing or a non-2xx status, treat as failure
    if (!resp || (typeof status === 'number' && status >= 400)) {
      if (status === 403) {
        toast.error(t('auth.forbidden', 'Forbidden'));
        return;
      }
      const msg =
        resp?.data?.message ||
        resp?.message ||
        t("students.delete_failed");
      toast.error(msg);
      return;
    }

    // OK: accept 200 or 204 as success
    if (status && ![200, 204].includes(status)) {
      // Any other weird status => fail
      toast.error(t("students.delete_failed"));
      return;
    }

    const successMessage = resp?.data?.message || t("students.delete_success");
    toast.success(successMessage);

    await store.dispatch("students/fetchStudents");
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden'));
    } else {
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t("students.delete_failed")
      );
    }
  }
};

// Prints EVERY student matching the current filters, not just the page
// currently on screen — a class roster is only useful printed in full, and
// the on-screen table is paginated. Fetched separately (via the report-data
// endpoint, which already returns the full unpaginated set) rather than
// reusing the paginated store list.
const isPrintingReport = ref(false);

const genderLabel = (g) => (g === "M" ? t("studentTable.male") : g === "F" ? t("studentTable.female") : "—");

function formatReportDate(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("ar", { year: "numeric", month: "long", day: "numeric" });
}

// Prints the full student list matching the current filters (basic info +
// guardian + current class/group/status) — no payment or attendance
// figures. Opens the print window synchronously (before the fetch below),
// since a browser only allows window.open() as a direct result of the
// click; opening it after an awaited fetch gets treated as a blocked popup.
const printStudentDataReport = async () => {
  const printWin = openPrintWindow();
  if (!printWin) {
    toast.error(t("students.print_popup_blocked"));
    return;
  }

  isPrintingReport.value = true;
  try {
    const params = { ...filters.value };
    const response = await api.get("/students/report-data", { params });
    const rows = response.data.data || [];

    const selectedClass = filters.value.class_id
      ? classes.value.find((c) => String(c.id) === String(filters.value.class_id))
      : null;

    const subtitleLines = [
      `${t("students.print_filter_class")}: ${selectedClass ? selectedClass.name : t("students.print_filter_all")}`,
      `${t("students.print_filter_gender")}: ${
        filters.value.gender ? genderLabel(filters.value.gender) : t("students.print_filter_all")
      }`,
      `${t("students.print_date")}: ${formatPrintDate()}`,
    ];

    printTable({
      title: t("students.print_list_title"),
      subtitleLines,
      columns: [
        { key: "id", label: t("studentTable.id") },
        { key: "full_name", label: t("studentTable.name") },
        { key: "national_num", label: t("studentTable.nationalOrPassport"), format: (r) => r.national_num || r.passport_num || "—" },
        { key: "gender", label: t("studentTable.gender"), format: (r) => genderLabel(r.gender) },
        { key: "birthdate", label: t("students.birthdate"), format: (r) => formatReportDate(r.birthdate) },
        { key: "phone", label: t("students.phone"), format: (r) => r.phone || "—" },
        { key: "address", label: t("students.address"), format: (r) => r.address || "—" },
        { key: "guardian_name", label: t("students.guardian_name"), format: (r) => r.guardian_name || "—" },
        { key: "guardian_phone", label: t("students.guardian_phone"), format: (r) => r.guardian_phone || "—" },
        { key: "class_name", label: t("students.class"), format: (r) => r.class_name || t("studentTable.notEnrolled") },
        { key: "group_name", label: t("students.group"), format: (r) => r.group_name || "—" },
        { key: "enrollment_date", label: t("students.enrollment_date"), format: (r) => formatReportDate(r.enrollment_date) },
      ],
      rows,
      orientation: "landscape",
    }, printWin);
  } catch (error) {
    printWin.close();
    notifyError(error, "students.data_report_failed");
  } finally {
    isPrintingReport.value = false;
  }
};

// Initial load
onMounted(() => {
  fetchStudents();
  store.dispatch("classes/fetchClasses");
});
</script>
